import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GetQuoteModal from '../../components/feature/GetQuoteModal';
import BrandWatermark from '../../components/feature/BrandWatermark';
import { productsData } from '../../mocks/products';
import { useSEO } from '../../utils/seo';

const categories = [
  { id: 'all', name: 'All Products', icon: 'ri-grid-line' },
  { id: 'laser-toner-cartridges', name: 'Laser Toner Cartridges', icon: 'ri-printer-line' },
  { id: 'generic-ink', name: 'Generic Ink', icon: 'ri-drop-line' },
  { id: 'gi2-premium-ink', name: 'Gi2 Premium Ink', icon: 'ri-ink-bottle-line' },
  { id: 'opc-drums', name: 'OPC Drums', icon: 'ri-disc-line' },
  { id: 'toner-powder', name: 'Toner Powder', icon: 'ri-flask-line' },
  { id: 'lamination-pouch', name: 'Lamination Pouch', icon: 'ri-file-shield-line' },
  { id: 'lamination-machine', name: 'Lamination Machine', icon: 'ri-tools-line' },
  { id: 'glossy-photo-paper', name: 'Glossy Photo Paper', icon: 'ri-image-line' },
  { id: 'printer-parts', name: 'Printer Parts', icon: 'ri-settings-3-line' },
  { id: 'inkjet-pvc-card', name: 'Inkjet PVC Card', icon: 'ri-bank-card-line' },
];

type ViewMode = 'grid' | 'list';

export default function ProductsPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showStickyPanel, setShowStickyPanel] = useState(true);
  const [hasClosedPanel, setHasClosedPanel] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    // Load saved view mode from localStorage (mobile only)
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      const saved = localStorage.getItem('productViewMode');
      return (saved as ViewMode) || 'grid';
    }
    return 'grid';
  });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gi2merchants.com';

  useSEO({
    title: 'Printer Consumables Products - Toner, Ink, OPC Drums | GI2 Merchants',
    description: 'Browse our complete range of B2B printer consumables: laser toner cartridges, generic ink, OPC drums, toner powder, lamination pouches, photo paper, and printer parts. Bulk pricing available across India.',
    keywords: 'printer products, toner cartridges, generic ink, OPC drums, toner powder, lamination pouch, photo paper, printer parts, B2B products',
    canonical: `${siteUrl}/products`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Printer Consumables Products',
      description: 'Complete B2B supply for printer consumables and lamination products. Bulk pricing available for all categories.',
      url: `${siteUrl}/products`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: productsData.slice(0, 10).map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.images[0],
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'INR',
              availability: product.availability === 'In Stock' ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
            },
          },
        })),
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Save view mode to localStorage (mobile only)
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      localStorage.setItem('productViewMode', viewMode);
    }
  }, [viewMode]);

  const handleClosePanel = () => {
    setShowStickyPanel(false);
    setHasClosedPanel(true);
  };

  const filteredProducts = activeCategory === 'all' 
    ? productsData 
    : productsData.filter(product => product.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-teal-50 to-white overflow-hidden">
        <BrandWatermark variant="subtle" position="top-right" size="large" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-40 h-40 border-2 border-teal-600 rotate-45"></div>
          <div className="absolute bottom-20 left-20 w-32 h-32 border-2 border-orange-500 rotate-12"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
              Our Product Range
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Complete B2B supply for printer consumables and lamination products. Bulk pricing available for all categories.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button
                onClick={() => setShowQuoteModal(true)}
                className="px-8 py-4 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Get Bulk Quote
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
              <a
                href="https://wa.me/919876543210?text=Hi, I need product information"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex items-center gap-3 overflow-x-auto py-4 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-teal-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-12">
          {/* Header with View Toggle (Mobile Only) */}
          <div className="mb-8 flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                {activeCategory === 'all' ? 'All Products' : categories.find(c => c.id === activeCategory)?.name}
              </h2>
              <p className="text-gray-600 mt-2">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} available
              </p>
            </div>

            {/* Mobile View Toggle - Only visible on mobile */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-teal-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-label="Grid view"
              >
                <i className="ri-grid-line text-lg"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-teal-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-label="List view"
              >
                <i className="ri-list-check text-lg"></i>
              </button>
            </div>
          </div>

          {/* Products Grid - Responsive based on viewMode */}
          <div className={`grid gap-6 ${
            viewMode === 'list' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
          }`}>
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                viewMode={viewMode}
                onClick={() => navigate(`/products/${product.id}`)}
              />
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                <i className="ri-inbox-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Products Found</h3>
              <p className="text-gray-600">Try selecting a different category</p>
            </div>
          )}

          {/* Trademark Disclaimer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              <i className="ri-information-line mr-1"></i>
              The Brand Name/Model Designations are the property of their respective owners and are used here for identification purposes only.
            </p>
          </div>
        </div>
      </section>

      {/* Sticky Enquiry Panel - Desktop Only */}
      {showStickyPanel && !hasClosedPanel && (
        <div className="hidden lg:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-64 border border-gray-200 relative">
            <button
              onClick={handleClosePanel}
              className="absolute -top-2 -right-2 w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-900 text-white rounded-full transition-colors duration-200 cursor-pointer shadow-lg"
              aria-label="Close panel"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
            <p className="text-sm text-gray-600 mb-4">Get instant quote for bulk orders</p>
            <button
              onClick={() => setShowQuoteModal(true)}
              className="w-full px-6 py-3 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </button>
          </div>
        </div>
      )}

      {showQuoteModal && (
        <GetQuoteModal onClose={() => setShowQuoteModal(false)} />
      )}
    </div>
  );
}

// Product Card Component with Image Carousel
interface ProductCardProps {
  product: typeof productsData[0];
  viewMode: ViewMode;
  onClick: () => void;
}

function ProductCard({ product, viewMode, onClick }: ProductCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Auto-cycle images on hover (desktop)
  useEffect(() => {
    if (isHovering && product.images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, product.images.length]);

  // Reset image index when not hovering (desktop)
  useEffect(() => {
    if (!isHovering) {
      const timeout = setTimeout(() => {
        setCurrentImageIndex(0);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isHovering]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next image
        setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      } else {
        // Swipe right - previous image
        setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
      }
    }
  };

  return (
    <div
      className={`group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-teal-200 ${
        viewMode === 'list' ? 'md:flex-row' : ''
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Product Image with Carousel */}
      <div
        ref={imageContainerRef}
        className={`relative w-full overflow-hidden bg-gray-50 ${
          viewMode === 'list' ? 'aspect-square' : 'aspect-square'
        }`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Images Container */}
        <div className="relative w-full h-full">
          {product.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`${product.name} - Image ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              } ${isHovering ? 'scale-110' : 'scale-100'} transition-transform duration-500`}
            />
          ))}
        </div>

        {/* Image Indicators */}
        {product.images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
            {product.images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentImageIndex
                    ? 'bg-teal-600 w-3'
                    : 'bg-white/70 hover:bg-white'
                }`}
                aria-label={`View image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {product.availability === 'In Stock' && (
          <div className="absolute top-3 right-3 px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full shadow-lg z-10">
            In Stock
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>

      {/* Product Info - Click to navigate */}
      <div
        onClick={onClick}
        className={`flex flex-col ${
          viewMode === 'list' ? 'p-4' : 'p-3 md:p-5'
        }`}
      >
        {/* Title and Icon Row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className={`font-bold text-gray-900 text-left line-clamp-2 flex-1 ${
            viewMode === 'list' ? 'text-sm' : 'text-xs md:text-base'
          }`}>
            {product.name}
          </h3>
          <div className={`flex items-center justify-center rounded-full bg-teal-50 flex-shrink-0 ${
            viewMode === 'list' ? 'w-10 h-10' : 'w-8 h-8 md:w-10 md:h-10'
          }`}>
            <i className={`${product.icon} text-teal-700 ${
              viewMode === 'list' ? 'text-xl' : 'text-base md:text-xl'
            }`}></i>
          </div>
        </div>

        {/* Description - Hidden in 2-column grid on mobile */}
        {viewMode === 'list' && (
          <p className="text-xs text-gray-600 line-clamp-2 text-left mb-3">
            {product.description}
          </p>
        )}
        <p className="hidden md:block text-sm text-gray-600 line-clamp-2 text-left mb-3">
          {product.description}
        </p>

        {/* Suitable For Tags */}
        <div className={`flex flex-wrap gap-1.5 ${
          viewMode === 'list' ? 'mb-3' : 'mb-2 md:mb-3'
        }`}>
          {product.suitableFor.slice(0, 2).map((type, idx) => (
            <span
              key={idx}
              className={`px-2 py-0.5 bg-teal-50 text-teal-700 font-medium rounded-full whitespace-nowrap ${
                viewMode === 'list' ? 'text-[10px]' : 'text-[9px] md:text-xs'
              }`}
            >
              {type}
            </span>
          ))}
          {product.suitableFor.length > 2 && (
            <span className={`px-2 py-0.5 bg-gray-100 text-gray-600 font-medium rounded-full whitespace-nowrap ${
              viewMode === 'list' ? 'text-[10px]' : 'text-[9px] md:text-xs'
            }`}>
              +{product.suitableFor.length - 2}
            </span>
          )}
        </div>

        {/* Footer Section */}
        <div className={`mt-auto ${
          viewMode === 'list' ? 'space-y-2' : 'space-y-1.5 md:space-y-0'
        }`}>
          <div className={`${
            viewMode === 'list' 
              ? 'flex items-center justify-between' 
              : 'flex items-center justify-between md:justify-between'
          }`}>
            <span className={`text-teal-700 font-semibold inline-flex items-center gap-1 whitespace-nowrap ${
              viewMode === 'list' ? 'text-xs' : 'text-[10px] md:text-sm'
            }`}>
              View Details
              <i className={`ri-arrow-right-line group-hover:translate-x-1 transition-transform ${
                viewMode === 'list' ? '' : 'text-xs md:text-base'
              }`}></i>
            </span>
            
            <span className={`text-gray-500 font-medium whitespace-nowrap ${
              viewMode === 'list' 
                ? 'text-[10px]' 
                : 'hidden md:inline text-xs'
            }`}>
              Quote-based
            </span>
          </div>

          {viewMode === 'grid' && (
            <div className="md:hidden">
              <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 font-medium rounded text-[9px] whitespace-nowrap">
                Quote-based
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
